import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ProductScreenshot from '../../components/products/ProductScreenshot';
import ProductIcon from '../../components/products/ProductIcon';
import { Seo } from '../../components/Seo';
import { getProductById, getProductsForMarket } from '../../data/products';
import { useMarket } from '../../context/MarketContext';

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { market } = useMarket();
  const product = productId ? getProductById(productId) : undefined;
  
  if (!product) {
    return <Navigate to="/products" replace />;
  }

  if (product.markets.includes('IN') && !product.markets.includes('US') && market === 'US') {
    return (
      <div className="pt-24 min-h-[70vh] flex items-center">
        <section className="w-full py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="card p-10">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-slate-100 dark:bg-dark-800 text-slate-500 dark:text-dark-400 flex items-center justify-center">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 4.5h.008v.008H12V16.5z" />
                </svg>
              </div>
              <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white mb-3">
                CimmpleRx is unavailable in your region
              </h1>
              <p className="text-slate-600 dark:text-dark-300 mb-8">
                CimmpleRx is currently offered only in India. Explore the Cimmple products available in your market.
              </p>
              <Link to="/products" className="btn-primary inline-flex">
                View Available Products
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const otherProducts = getProductsForMarket(market).filter(({ id }) => id !== product.id);

  return (
    <div className="pt-24">
      {product.id === 'cimmpleflow' && (
        <Seo
          title="CimmpleFlow | Cloud Operating System for Machine Shops"
          description="CimmpleFlow is a modern cloud operating system for machine shops that includes a complete manufacturing ERP—quoting, production, quality, inventory, accounting, and AI automation."
        />
      )}
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-slate-500 dark:text-dark-400">
              <li><Link to="/" className="hover:text-slate-900 dark:hover:text-white transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/products" className="hover:text-slate-900 dark:hover:text-white transition-colors">Products</Link></li>
              <li>/</li>
              <li className="text-slate-900 dark:text-white font-medium">{product.name}</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`w-24 h-24 rounded-3xl ${
                product.color === 'primary' 
                  ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400' 
                  : 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-400'
              } flex items-center justify-center mb-8`}>
                <ProductIcon icon={product.icon} className="w-16 h-16" />
              </div>
              
              <h1 className="text-4xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">
                {product.name}
              </h1>
              <p className={`text-xl font-medium mb-6 ${
                product.color === 'primary' ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'
              }`}>
                {product.tagline}
              </p>
              <p className="text-slate-600 dark:text-dark-300 text-lg whitespace-pre-line">
                {product.longDescription}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                {product.id === 'cimmpleflow' ? (
                  <>
                    <Link to="/contact" className="btn-primary">
                      Book a Demo
                    </Link>
                    <Link to="/pricing?product=cimmple" className="btn-secondary">
                      Start Free Trial
                    </Link>
                  </>
                ) : (
                  <>
                    <Link 
                      to={`/pricing?product=${product.id}`}
                      className="btn-primary"
                    >
                      View Pricing
                    </Link>
                    <Link to="/contact" className="btn-secondary">
                      Request Demo
                    </Link>
                  </>
                )}
              </div>
            </div>

            {/* Benefits Card */}
            <div className="card p-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">Key Benefits</h3>
              <ul className="space-y-4">
                {product.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${
                      product.color === 'primary' 
                        ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400' 
                        : 'bg-accent-100 dark:bg-accent-500/20 text-accent-600 dark:text-accent-400'
                    }`}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-slate-600 dark:text-dark-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {product.screenshot && product.screenshotAlt && (
        <section className="pb-16 sm:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              <div
                className={`absolute -inset-4 sm:-inset-6 rounded-[2rem] blur-2xl pointer-events-none ${
                  product.color === 'primary'
                    ? 'bg-gradient-to-br from-primary-500/10 via-transparent to-primary-500/5'
                    : 'bg-gradient-to-br from-accent-500/10 via-transparent to-accent-500/5'
                }`}
                aria-hidden="true"
              />
              <ProductScreenshot
                imageSrc={product.screenshot}
                imageAlt={product.screenshotAlt}
                productName={product.name}
                color={product.color}
              />
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              Powerful <span className="gradient-text">Features</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Everything you need to manage your operations effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {product.features.map((feature, index) => (
              <div key={index} className="card group hover:border-primary-500/30">
                <div className={`w-12 h-12 rounded-xl ${
                  product.color === 'primary' 
                    ? 'bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 group-hover:bg-primary-200 dark:group-hover:bg-primary-500/20' 
                    : 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-400 group-hover:bg-accent-200 dark:group-hover:bg-accent-500/20'
                } flex items-center justify-center mb-4 transition-colors duration-300`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-dark-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title mb-6">
                Perfect for your <span className="gradient-text">industry</span>
              </h2>
              <p className="text-slate-600 dark:text-dark-300 text-lg mb-8">
                {product.name} is designed for teams across these industries.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {product.useCases.map((useCase, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-slate-100 dark:bg-dark-800/30">
                    <svg className={`w-5 h-5 ${
                      product.color === 'primary' ? 'text-primary-600 dark:text-primary-500' : 'text-accent-600 dark:text-accent-500'
                    }`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-slate-700 dark:text-dark-300 text-sm">{useCase}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Card */}
            <div className="card p-8 text-center relative overflow-hidden">
              <div className={`absolute inset-0 bg-gradient-to-br ${
                product.color === 'primary' 
                  ? 'from-primary-500/5 to-primary-500/10' 
                  : 'from-accent-500/5 to-accent-500/10'
              }`} />
              <div className="relative">
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4">
                  Ready to get started?
                </h3>
                <p className="text-slate-600 dark:text-dark-300 mb-8">
                  {product.id === 'cimmpleflow'
                    ? 'See how a modern cloud operating system runs your entire machine shop.'
                    : `Start using ${product.name} today and transform your business.`}
                </p>
                <Link to="/contact" className="btn-primary">
                  {product.id === 'cimmpleflow' ? 'Book a Demo' : 'Request a Demo'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {market === 'US' && product.markets.includes('US') && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 bg-gradient-to-br from-slate-50 to-primary-50/30 dark:from-dark-800/50 dark:to-primary-500/5 border-primary-500/20">
              <div>
                <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">
                  Don't want to do the data entry?
                </h2>
                <p className="text-slate-600 dark:text-dark-300">
                  Our back-office service receives your invoices and order info via email (including PDFs), enters everything into your {product.name} subscription, and keeps your records up to date—so you can focus on core operations.
                </p>
              </div>
              <Link
                to="/services/back-office"
                className={`btn-secondary flex-shrink-0 inline-flex items-center justify-center gap-2 ${
                  product.color === 'primary' ? 'hover:border-primary-500/50' : 'hover:border-accent-500/50'
                }`}
              >
                Learn about Back-Office
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      )}

      {otherProducts.length > 0 && (
        <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-8">
              Explore Our Other Products
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {otherProducts.map((otherProduct) => (
                <Link
                  key={otherProduct.id}
                  to={`/products/${otherProduct.id}`}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  View {otherProduct.name}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
